import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowDown, ChevronDown, MapPin, Share2, SlidersHorizontal, X } from 'lucide-react';
import { locationOptions, offlineListings } from './offlineListingData';
import ImageWithLoader from '../components/ImageWithLoader';

const sortOptions = [
    { value: 'priceLowToHigh', label: 'Price Low to High' },
    { value: 'priceHighToLow', label: 'Price High to Low' },
    { value: 'discountHighToLow', label: 'Discount High to Low' },
    { value: 'discountLowToHigh', label: 'Discount Low to High' }
];
const validSortValues = sortOptions.map((option) => option.value);

const getPriceValue = (price) => {
    const numeric = Number(String(price || '').replace(/[^0-9.]/g, ''));
    return Number.isFinite(numeric) ? numeric : 0;
};
const copyToClipboardFallback = (value) => {
    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'absolute';
    textArea.style.left = '-9999px';
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
};

function OfflineHome() {
    const routeLocation = useLocation();
    const navigate = useNavigate();
    const urlParams = useMemo(
        () => new URLSearchParams(routeLocation.search),
        [routeLocation.search]
    );

    const allLocationOptions = useMemo(() => {
        const fromListings = offlineListings
            .map((listing) => String(listing.location || '').trim())
            .filter(Boolean);
        return Array.from(new Set([...locationOptions, ...fromListings]));
    }, []);

    const propertyTypeOptions = useMemo(() => {
        const fromListings = offlineListings
            .map((listing) => String(listing.propertyType || '').trim())
            .filter(Boolean);
        return ['All', ...Array.from(new Set(fromListings)).filter((item) => item !== 'All')];
    }, []);

    const listingTypeOptions = useMemo(() => {
        const fromListings = offlineListings
            .map((listing) => String(listing.listingType || '').trim())
            .filter(Boolean);
        return ['All', ...Array.from(new Set(fromListings)).filter((item) => item !== 'All')];
    }, []);

    const getSafeLocation = useCallback((value) => {
        const normalized = String(value || '').trim();
        if (!normalized || normalized === 'All Locations') {
            return 'All Locations';
        }
        return allLocationOptions.includes(normalized) ? normalized : 'All Locations';
    }, [allLocationOptions]);

    const getSafePropertyType = useCallback((value) => {
        const normalized = String(value || '').trim();
        return propertyTypeOptions.includes(normalized) ? normalized : 'All';
    }, [propertyTypeOptions]);
    const getSafeListingType = useCallback((value) => {
        const normalized = String(value || '').trim();
        return listingTypeOptions.includes(normalized) ? normalized : 'All';
    }, [listingTypeOptions]);
    const getSafeSortBy = (value) => (validSortValues.includes(value) ? value : '');

    const [showDesktopPopup, setShowDesktopPopup] = useState(false);
    const [location, setLocation] = useState(() =>
        getSafeLocation(urlParams.get('location') || 'All Locations')
    );
    const [propertyType, setPropertyType] = useState(() =>
        getSafePropertyType(urlParams.get('propertyType') || 'All')
    );
    const [listingType, setListingType] = useState(() =>
        getSafeListingType(urlParams.get('listingType') || 'All')
    );
    const [showSortSidebar, setShowSortSidebar] = useState(false);
    const [sortBy, setSortBy] = useState(() => getSafeSortBy(urlParams.get('sort') || ''));
    const [shareStatus, setShareStatus] = useState('');

    const syncUrlParams = useCallback(
        (nextLocation, nextPropertyType, nextListingType, nextSortBy) => {
            const nextParams = new URLSearchParams();

            if (nextLocation !== 'All Locations') {
                nextParams.set('location', nextLocation);
            }
            if (nextPropertyType !== 'All') {
                nextParams.set('propertyType', nextPropertyType);
            }
            if (nextListingType !== 'All') {
                nextParams.set('listingType', nextListingType);
            }
            if (nextSortBy) {
                nextParams.set('sort', nextSortBy);
            }

            const nextSearch = nextParams.toString();
            const currentSearch = routeLocation.search.startsWith('?')
                ? routeLocation.search.slice(1)
                : routeLocation.search;

            if (nextSearch !== currentSearch) {
                navigate(
                    {
                        pathname: '/offline-listing',
                        search: nextSearch ? `?${nextSearch}` : ''
                    },
                    { replace: true }
                );
            }
        },
        [navigate, routeLocation.search]
    );

    const handleLocationChange = useCallback(
        (event) => {
            const value = event.target.value;
            setLocation(value);
            syncUrlParams(value, propertyType, listingType, sortBy);
        },
        [propertyType, listingType, sortBy, syncUrlParams]
    );

    const handlePropertyTypeChange = useCallback(
        (event) => {
            const value = event.target.value;
            setPropertyType(value);
            syncUrlParams(location, value, listingType, sortBy);
        },
        [location, listingType, sortBy, syncUrlParams]
    );

    const handleListingTypeChange = useCallback(
        (event) => {
            const value = event.target.value;
            setListingType(value);
            syncUrlParams(location, propertyType, value, sortBy);
        },
        [location, propertyType, sortBy, syncUrlParams]
    );

    const handleSortChange = useCallback(
        (value) => {
            setSortBy(value);
            setShowSortSidebar(false);
            syncUrlParams(location, propertyType, listingType, value);
        },
        [location, propertyType, listingType, syncUrlParams]
    );
    const handleSharePageLink = useCallback(async () => {
        const pageUrl = `${window.location.origin}${routeLocation.pathname}${routeLocation.search}`;

        try {
            if (navigator.share) {
                await navigator.share({
                    title: 'Below Market Property Deals',
                    url: pageUrl
                });
                setShareStatus('Page link shared.');
                setShowSortSidebar(false);
                return;
            }
        } catch (error) {
            if (error && error.name === 'AbortError') {
                return;
            }
        }

        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(pageUrl);
                setShareStatus('Page link copied.');
                setShowSortSidebar(false);
                return;
            }
        } catch (error) {
            // Continue to legacy fallback copy behavior.
        }

        const copied = copyToClipboardFallback(pageUrl);
        setShareStatus(copied ? 'Page link copied.' : 'Unable to share this page link.');
        setShowSortSidebar(false);
    }, [routeLocation.pathname, routeLocation.search]);

    const navigateToListingDetails = useCallback(
        (listingId) => {
            navigate(`/offline-listing/details/${encodeURIComponent(listingId)}`);
        },
        [navigate]
    );

    useEffect(() => {
        const mediaQuery = window.matchMedia('(min-width: 769px)');

        const handleScreenChange = (event) => {
            setShowDesktopPopup(event.matches);
        };

        setShowDesktopPopup(mediaQuery.matches);

        if (mediaQuery.addEventListener) {
            mediaQuery.addEventListener('change', handleScreenChange);
        } else {
            mediaQuery.addListener(handleScreenChange);
        }

        return () => {
            if (mediaQuery.removeEventListener) {
                mediaQuery.removeEventListener('change', handleScreenChange);
            } else {
                mediaQuery.removeListener(handleScreenChange);
            }
        };
    }, []);

    useEffect(() => {
        document.body.style.overflow = showSortSidebar ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [showSortSidebar]);
    useEffect(() => {
        if (!shareStatus) {
            return undefined;
        }
        const timeoutId = window.setTimeout(() => {
            setShareStatus('');
        }, 2500);
        return () => {
            window.clearTimeout(timeoutId);
        };
    }, [shareStatus]);

    useEffect(() => {
        const urlLocation = getSafeLocation(urlParams.get('location') || 'All Locations');
        const urlPropertyType = getSafePropertyType(urlParams.get('propertyType') || 'All');
        const urlListingType = getSafeListingType(urlParams.get('listingType') || 'All');
        const urlSortBy = getSafeSortBy(urlParams.get('sort') || '');

        setLocation((previous) => (previous === urlLocation ? previous : urlLocation));
        setPropertyType((previous) =>
            previous === urlPropertyType ? previous : urlPropertyType
        );
        setListingType((previous) =>
            previous === urlListingType ? previous : urlListingType
        );
        setSortBy((previous) => (previous === urlSortBy ? previous : urlSortBy));
    }, [urlParams, getSafeLocation, getSafePropertyType, getSafeListingType]);

    const filteredListings = useMemo(() => {
        const filtered = offlineListings.filter((listing) => {
            const matchLocation = location === 'All Locations' || listing.location === location;
            const matchPropertyType = propertyType === 'All' || listing.propertyType === propertyType;
            const matchListingType = listingType === 'All' || listing.listingType === listingType;

            return matchLocation && matchPropertyType && matchListingType;
        });

        const sorted = [...filtered];

        if (sortBy === 'priceLowToHigh') {
            sorted.sort((a, b) => getPriceValue(a.newPrice) - getPriceValue(b.newPrice));
        } else if (sortBy === 'priceHighToLow') {
            sorted.sort((a, b) => getPriceValue(b.newPrice) - getPriceValue(a.newPrice));
        } else if (sortBy === 'discountHighToLow') {
            sorted.sort((a, b) => (b.discountPercent || 0) - (a.discountPercent || 0));
        } else if (sortBy === 'discountLowToHigh') {
            sorted.sort((a, b) => (a.discountPercent || 0) - (b.discountPercent || 0));
        }

        return sorted;
    }, [location, propertyType, listingType, sortBy]);
    const selectedSortLabel = useMemo(
        () => sortOptions.find((option) => option.value === sortBy)?.label || 'Default Sorting',
        [sortBy]
    );
    const activeFilterCount = useMemo(() => {
        let count = 0;
        if (location !== 'All Locations') {
            count += 1;
        }
        if (propertyType !== 'All') {
            count += 1;
        }
        if (listingType !== 'All') {
            count += 1;
        }
        if (sortBy) {
            count += 1;
        }
        return count;
    }, [location, propertyType, listingType, sortBy]);

    if (showDesktopPopup) {
        return (
            <div className="flex min-h-screen items-center justify-center bg-[rgba(15,23,35,0.92)] px-6 text-center">
                <div className="w-full max-w-[420px] rounded-2xl bg-white px-6 py-7 shadow-[0_14px_30px_rgba(20,38,53,0.2)]">
                    <h2 className="mb-2 text-[26px] font-semibold text-[#243445]">Mobile Only Access</h2>
                    <p className="text-base leading-[1.4] text-[#536172]">
                        This page is available only on mobile devices. Please open it on your phone.
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="relative min-h-screen overflow-x-hidden bg-[#eef3f8] text-[#1f2f41]"
            style={{ fontFamily: '"Poppins", "Segoe UI", sans-serif' }}
        >
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute -right-24 -top-20 h-72 w-72 rounded-full bg-[radial-gradient(circle,_rgba(49,130,206,0.28)_0%,_rgba(49,130,206,0)_70%)]" />
                <div className="absolute -left-20 top-[34%] h-72 w-72 rounded-full bg-[radial-gradient(circle,_rgba(34,197,94,0.2)_0%,_rgba(34,197,94,0)_72%)]" />
            </div>

            {showSortSidebar && (
                <button
                    type="button"
                    aria-label="Close filters"
                    className="fixed inset-0 z-30 bg-[rgba(7,22,39,0.52)] backdrop-blur-[2px]"
                    onClick={() => setShowSortSidebar(false)}
                />
            )}

            <aside
                id="offline-sort-sidebar"
                className={`fixed left-0 top-0 z-40 h-screen w-[82%] max-w-[290px] border-r border-[#d3e1ef] bg-[#f7fbff] shadow-[0_18px_40px_rgba(9,35,64,0.26)] transition-transform duration-300 ${showSortSidebar ? 'translate-x-0' : '-translate-x-full'}`}
                aria-label="Sort sidebar"
            >
                <div className="h-full overflow-y-auto px-4 pb-5 pt-4">
                    <div className="mb-4 rounded-xl bg-[linear-gradient(135deg,#0b84ff,#2fb6ff)] p-3 text-white">
                        <div className="mb-2 flex items-center justify-between">
                            <h2 className="text-sm font-semibold uppercase tracking-[0.7px]">Sort & Share</h2>
                            <button
                                type="button"
                                onClick={() => setShowSortSidebar(false)}
                                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[rgba(255,255,255,0.2)] text-white"
                                aria-label="Close sidebar"
                            >
                                <X size={18} />
                            </button>
                        </div>
                        <p className="text-xs leading-[1.35] text-[rgba(255,255,255,0.92)]">
                            Current sort: {selectedSortLabel}
                        </p>
                    </div>

                    <ul className="space-y-2">
                        {sortOptions.map((option) => (
                            <li key={option.value}>
                                <button
                                    type="button"
                                    onClick={() => handleSortChange(option.value)}
                                    className={`flex w-full items-center justify-between rounded-xl border px-3 py-2.5 text-left text-[14px] transition-colors ${sortBy === option.value ? 'border-[#9ad2ff] bg-[#e9f6ff] font-semibold text-[#0d5f9f]' : 'border-[#dce7f2] bg-white text-[#385068] hover:border-[#b9d7ef]'}`}
                                >
                                    <span>{option.label}</span>
                                    <span
                                        className={`h-3 w-3 rounded-full border ${sortBy === option.value ? 'border-[#0b84ff] bg-[#0b84ff]' : 'border-[#b6c6d7] bg-transparent'}`}
                                    />
                                </button>
                            </li>
                        ))}
                    </ul>

                    <div className="mt-4 space-y-2 border-t border-[#dce7f2] pt-4">
                        <button
                            type="button"
                            onClick={() => handleSortChange('')}
                            className="w-full rounded-xl border border-[#d5dfeb] bg-white px-3 py-2 text-[13px] font-semibold text-[#44566b]"
                        >
                            Reset Sort
                        </button>
                        <button
                            type="button"
                            onClick={handleSharePageLink}
                            className="flex w-full items-center justify-center gap-2 rounded-xl border border-[#aad7f7] bg-[#ebf7ff] px-3 py-2.5 text-[14px] font-semibold text-[#116ca8]"
                        >
                            <Share2 size={16} />
                            <span>Share Page Link</span>
                        </button>
                        {shareStatus && (
                            <p className="text-center text-xs font-medium text-[#116ca8]">
                                {shareStatus}
                            </p>
                        )}
                    </div>
                </div>
            </aside>

            <div className="relative mx-auto w-full max-w-[560px] pb-8">
                <header className="sticky top-0 z-20 border-b border-[rgba(175,197,219,0.55)] bg-[rgba(241,247,253,0.92)] backdrop-blur-md">
                    <div className="px-3 pb-3 pt-4">
                        <div className="flex items-start justify-between gap-3">
                            <div>
                                <h1 className="m-0 text-[20px] font-semibold leading-[1.2] tracking-[0.2px] text-[#0f3d66]">
                                    Below Market Deals
                                </h1>
                                <p className="mt-1 text-[12px] font-medium uppercase tracking-[0.7px] text-[#4f7090]">
                                    Curated opportunities in Dubai
                                </p>
                            </div>
                            <button
                                type="button"
                                onClick={() => setShowSortSidebar(true)}
                                className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-[#b8d2e8] bg-white text-[#3a5f7f] shadow-[0_8px_18px_rgba(18,60,95,0.12)]"
                                aria-label="Open filters"
                                aria-controls="offline-sort-sidebar"
                            >
                                <SlidersHorizontal size={18} />
                            </button>
                        </div>
                    </div>
                </header>

                <section className="px-3 pb-2 pt-4" aria-label="Property filters">
                    <div className="rounded-2xl border border-[#d2e2f1] bg-[rgba(255,255,255,0.92)] p-3 shadow-[0_14px_30px_rgba(14,52,86,0.1)]">
                        <div className="mb-2 flex items-center justify-between">
                            <p className="text-[11px] font-semibold uppercase tracking-[0.7px] text-[#4f7393]">
                                Refine Deals
                            </p>
                            <span className="rounded-full bg-[#e9f5ff] px-2.5 py-1 text-[11px] font-semibold text-[#116ca8]">
                                {filteredListings.length} results
                            </span>
                        </div>

                        <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.6px] text-[#6f88a0]">
                            Location
                        </label>
                        <div className="relative mb-3">
                            <select
                                value={location}
                                onChange={handleLocationChange}
                                className="min-h-11 w-full appearance-none rounded-xl border border-[#cfe0ef] bg-[#f8fbff] px-[14px] pr-9 text-[14px] font-semibold text-[#315170]"
                            >
                                <option value="All Locations">All Locations</option>
                                {allLocationOptions.map((option) => (
                                    <option key={option} value={option}>
                                        {option}
                                    </option>
                                ))}
                            </select>
                            <ChevronDown size={18} className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 text-[#7c94ab]" />
                        </div>

                        <div className="flex gap-3">
                            <div className="flex-1">
                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.6px] text-[#6f88a0]">
                                    Property
                                </label>
                                <div className="relative">
                                    <select
                                        value={propertyType}
                                        onChange={handlePropertyTypeChange}
                                        className="min-h-11 w-full appearance-none rounded-xl border border-[#cfe0ef] bg-[#f8fbff] px-[14px] pr-9 text-[14px] font-semibold text-[#315170]"
                                    >
                                        {propertyTypeOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option === 'All' ? 'All Properties' : option}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown size={18} className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 text-[#7c94ab]" />
                                </div>
                            </div>

                            <div className="flex-1">
                                <label className="mb-1 block text-[11px] font-semibold uppercase tracking-[0.6px] text-[#6f88a0]">
                                    Listing
                                </label>
                                <div className="relative">
                                    <select
                                        value={listingType}
                                        onChange={handleListingTypeChange}
                                        className="min-h-11 w-full appearance-none rounded-xl border border-[#cfe0ef] bg-[#f8fbff] px-[14px] pr-9 text-[14px] font-semibold text-[#315170]"
                                    >
                                        {listingTypeOptions.map((option) => (
                                            <option key={option} value={option}>
                                                {option === 'All' ? 'All Projects' : option}
                                            </option>
                                        ))}
                                    </select>
                                    <ChevronDown size={18} className="pointer-events-none absolute right-[12px] top-1/2 -translate-y-1/2 text-[#7c94ab]" />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="px-3 pb-2 pt-1" aria-label="Applied filters">
                    <div className="flex items-center justify-between rounded-xl border border-[#d6e5f3] bg-[rgba(255,255,255,0.75)] px-3 py-2">
                        <p className="text-xs font-semibold uppercase tracking-[0.6px] text-[#5a7692]">
                            {activeFilterCount} active filters
                        </p>
                        <p className="text-xs font-semibold text-[#296697]">{selectedSortLabel}</p>
                    </div>
                </section>

                <section className="grid gap-5 px-3 pb-4 pt-2" aria-label="Listings">
                    {filteredListings.length === 0 && (
                        <div className="rounded-2xl border border-[#d4e1ed] bg-white px-4 py-8 text-center shadow-[0_12px_24px_rgba(12,42,68,0.08)]">
                            <p className="text-sm font-semibold text-[#57708a]">
                                No listings found for the selected filters.
                            </p>
                        </div>
                    )}

                    {filteredListings.map((listing) => (
                        <article
                            key={listing.id}
                            className="group cursor-pointer overflow-hidden rounded-2xl border border-[#cfe0ed] bg-white shadow-[0_14px_28px_rgba(13,44,72,0.12)] transition-transform duration-300 active:scale-[0.99]"
                            role="button"
                            tabIndex={0}
                            onClick={() => navigateToListingDetails(listing.id)}
                            onKeyDown={(event) => {
                                if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    navigateToListingDetails(listing.id);
                                }
                            }}
                        >
                            <div className="relative m-[8px] overflow-hidden rounded-[14px]">
                                <ImageWithLoader
                                    src={listing.image}
                                    alt={listing.title}
                                    className="block aspect-[16/9] w-full object-cover"
                                    wrapperClassName="rounded-[14px]"
                                />
                                <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-[linear-gradient(180deg,rgba(7,24,38,0)_0%,rgba(7,24,38,0.75)_100%)]" />
                                <span className="absolute left-2 top-2 rounded-full bg-[rgba(255,255,255,0.92)] px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.5px] text-[#0f3d66]">
                                    Hot Deal
                                </span>
                                <span className="absolute right-2 top-2 rounded-xl bg-[linear-gradient(135deg,#1bbf5a,#37d277)] px-2.5 py-1.5 text-white shadow-[0_6px_16px_rgba(17,123,60,0.45)]">
                                    <small className="block text-[10px] font-semibold uppercase leading-none tracking-[0.4px]">
                                        Reduced
                                    </small>
                                    <strong className="mt-0.5 flex items-center gap-0.5 text-[19px] font-semibold leading-none">
                                        <span>{listing.discountPercent || 0}%</span>
                                        <ArrowDown size={16} strokeWidth={3} />
                                    </strong>
                                </span>
                                <span className="absolute bottom-2 left-2 inline-flex items-center gap-1 rounded-full bg-[rgba(255,255,255,0.92)] px-2.5 py-1 text-[11px] font-semibold text-[#204768]">
                                    <MapPin size={12} />
                                    {listing.location || 'Dubai'}
                                </span>
                            </div>

                            <div className="px-3 pb-4 pt-1">
                                <div className="flex items-center justify-between gap-2">
                                    <p className="m-0 text-[13px] font-medium text-[#7f8d9c] line-through">
                                        {listing.oldPrice}
                                    </p>
                                    <p className="m-0 text-[25px] font-bold tracking-[-0.3px] text-[#123a5d]">
                                        {listing.newPrice}
                                    </p>
                                </div>

                                <p className="mt-2 line-clamp-2 text-[14px] font-semibold leading-[1.4] text-[#344f68]">
                                    {listing.title}
                                </p>

                                <div className="mt-3 grid grid-cols-2 gap-2">
                                    <span className="rounded-lg bg-[#f0f7ff] px-2 py-1 text-[11px] font-semibold uppercase text-[#3d6990]">
                                        {listing.propertyType || 'Residential'}
                                    </span>
                                    <span className="rounded-lg bg-[#ecfff2] px-2 py-1 text-[11px] font-semibold uppercase text-[#2e7b4f]">
                                        {listing.listingType || 'Buy'}
                                    </span>
                                </div>

                                <button
                                    type="button"
                                    onClick={(event) => {
                                        event.stopPropagation();
                                        navigateToListingDetails(listing.id);
                                    }}
                                    className="mt-3 h-[46px] w-full rounded-xl bg-[linear-gradient(135deg,#0b84ff,#26a4ff)] px-4 text-[14px] font-bold uppercase tracking-[0.5px] text-white shadow-[0_10px_18px_rgba(11,132,255,0.35)]"
                                >
                                    View Details
                                </button>
                            </div>
                        </article>
                    ))}
                </section>
            </div>
        </div>
    );
}

export default OfflineHome;
